import {AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ConfigService} from "./config.service";
import {AuthEvents} from "../mvp-auth/services/auth-events.service";
import {AuthStateEnum} from "../mvp-auth";
import {filter, Subject, Subscription, takeUntil} from "rxjs";
import {LangChangeEvent, TranslateService} from "@ngx-translate/core";
import {SpinnerService} from "./services/shared/spinner.service";
import {SidenavToggleService} from "./services/shared/sidenav-toggle.service";
import {ActivatedRoute, ActivationEnd, NavigationEnd, NavigationStart, Router} from "@angular/router";
import {UserEmailService} from "./services/shared/user-email.service";
import {UserSignaturesService} from './services/shared/user-signatures.service';
import {BreakpointObserver, BreakpointState} from "@angular/cdk/layout";
import {navbarDataAdmin, navbarDataUser} from "./components/container/sidenav/nav-data";
import {PrevPageService} from "./services/component/navigation/prev-page.service";
import {MatSidenavContent} from "@angular/material/sidenav";

enum AppStateEnum {
  Boot,
  AutologinCheck,
  LoginProgress,
  Ready,
}

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly destroyed$ = new Subject<void>();
  authState: AuthStateEnum = AuthStateEnum.Unknown;
  appState: AppStateEnum = AppStateEnum.Boot;

  title = 'dms-mvp-frontend';

  isProduction: boolean;

  spinnerVisibility: boolean = false;
  isAdminUser = false; // TODO: Set this based on user roles
  private spinnerSubscription: Subscription;

  selectedLang = 'hu';

  languages = [
    { code: 'hu', name: 'Magyar' },
    { code: 'en', name: 'English' },
    { code: 'de', name: 'Deutsch' },
    { code: 'fr', name: 'Français' }
  ];

  noHeader: boolean = false;

  selectedTabIndex: number = 0;

  isSmallScreen: boolean = false;

  isAdminPage = false;

  private langChangeSub!: Subscription;

  // isBackToList: boolean = false;

  @ViewChild(MatSidenavContent, { static: true }) sidenavContent!: MatSidenavContent;
  private routeKey = '';

  constructor(private config: ConfigService,
              private readonly authEvents: AuthEvents,
              private translateService: TranslateService,
              public spinner: SpinnerService,
              public sidenavToggle: SidenavToggleService,
              private cd: ChangeDetectorRef,
              private router: Router,
              public userEmailService: UserEmailService,
              public userSignaturesService: UserSignaturesService,
              private breakpointObserver: BreakpointObserver,
              private cdr: ChangeDetectorRef,
              private prevPageService: PrevPageService,
              private route: ActivatedRoute,
  ) {
    this.isProduction = config.isProduction;
    this.title = this.isProduction ? 'FLEX' : 'FLEX - Dev';
    this.translateService.setDefaultLang('hu');
    this.translateService.use(localStorage.getItem('lang') || 'hu');
    this.spinnerSubscription = this.spinner.visibility$.subscribe((visibility) => {
      this.spinnerVisibility = visibility;
      this.cd.markForCheck();
    });
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.isAdminPage = window.location.href.includes('/admin');
        this.updateSelectedTab(event.urlAfterRedirects);
        // this.updateNavigationButton(event.urlAfterRedirects);
      }
    });
  }

  ngOnInit(): void {
    this.authEvents.authState$.pipe(
      takeUntil(this.destroyed$),
    ).subscribe(state => {
      this.authState = state;
      if (state === AuthStateEnum.AutoLogin) {
        this.appState = AppStateEnum.AutologinCheck;
      } else if (state === AuthStateEnum.LoginCompleted) {
        this.appState = AppStateEnum.Ready;
      } else if (this.appState !== AppStateEnum.Boot) {
        this.appState = AppStateEnum.LoginProgress;
      }
      // if (state === AuthStateEnum.LoginNeeded) {
      //   this.router.navigate(['dev', 'login']);
      // } else if (state === AuthStateEnum.LoginCompleted) {
      //   this.router.navigate(['dashboard']);
      // } else if (state === AuthStateEnum.LoginAborted) {
      //   this.router.navigate(['']);
      // }
    });
    this.selectedLang = localStorage.getItem('lang') || 'hu';
    this.translateService.use(this.selectedLang);
    this.langChangeSub = this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.selectedLang = event.lang;
    });
    this.breakpointObserver.observe([
      "(max-width: 768px)"
    ]).subscribe((result: BreakpointState) => {
      if(result.breakpoints["(max-width: 768px)"]) {
        this.isSmallScreen = true;
        this.cdr.detectChanges();
      } else {
        this.isSmallScreen = false;
        this.cdr.detectChanges();
      }
    });
    this.router.events.subscribe(event => {
      if(event instanceof ActivationEnd){
        if(window.location.href.includes('login')){
          this.noHeader = true;
        } else {
          this.noHeader = false;
        }
      }
    });
    this.prevPageService.scroll$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.scrollDown());
  }

  ngAfterViewInit() {
    this.routeKey = this.router.url;

    this.router.events
      .pipe(filter(e => e instanceof NavigationStart), takeUntil(this.destroyed$))
      .subscribe(() => {
        const scrollTop = this.sidenavContent.getElementRef().nativeElement.scrollTop;
        this.prevPageService.saveScrollRoutePosition(this.routeKey, scrollTop);
      });

    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd), takeUntil(this.destroyed$))
      .subscribe(() => {
        this.routeKey = this.router.url;

        const savedPosition = this.prevPageService.getScrollRoutePosition(this.routeKey);
        if (savedPosition !== undefined) {
          setTimeout(() => {
            this.sidenavContent.getElementRef().nativeElement.scrollTop = savedPosition;
          });
        }
      });
  }

  ngOnDestroy(): void {
    this.spinnerSubscription.unsubscribe();
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  updateSelectedTab(url: string): void {
    if (url.startsWith('/dashboard')) {
      this.selectedTabIndex = 1;
    } else if (url.startsWith('/document/list')) {
      this.selectedTabIndex = 2;
    } else {
      this.selectedTabIndex = 0;
    }
  }

  // updateNavigationButton(url: string): void {
  //     if (url.startsWith('/task')) {
  //       this.isBackToList = true;
  //     } else this.isBackToList = false;
  // }

  onTabChange(index: number): void {
    if (index === 1) {
      this.router.navigate(['/dashboard']);
    } else if (index === 2) {
      this.router.navigate(['/document/list']);
    }
  }

  logOut(){
    this.userEmailService.emailAddress=''; //todo: ez csak ideiglenes, ki kell jelentkeztetni a felhasználót
    this.userEmailService.setLoggedInEmail$('');
    this.userSignaturesService.setAvailableSignatures$([]);
    this.router.navigate(['/login']);
  }

  openSidenav(){
    if(this.isSmallScreen || this.isAdminPage) {
      this.sidenavToggle.isShowing = true;
    }
  }

  changeLanguage(lang: string) {
    this.selectedLang = lang;

    localStorage.setItem('lang', this.selectedLang);

    this.translateService.use(this.selectedLang)
  }

  onLogoClicked(){
    const prevRoute = '/dashboard';
    this.prevPageService.setPreviousRoute(prevRoute);
    this.router.navigate(['/pageloading']);
  }

  isAdmin(){
    if(this.isAdminPage){
      return navbarDataAdmin;
    } else {
      return navbarDataUser;
    }
  }

  getLanguage(){
    const language = this.languages.find(language =>
      language.code === this.selectedLang);
    return language ? language.name : this.selectedLang;
  }

  scrollDown() {
    const scrollHeight = this.sidenavContent.getElementRef().nativeElement.scrollHeight;
    setTimeout(() => {
      this.sidenavContent.getElementRef().nativeElement.scrollTop = scrollHeight;
    });
  }

  protected readonly AuthStateEnum = AuthStateEnum;

}
